/**
 * Frontend Error Logger
 *
 * Exposes window.logFrontendError(payload) and registers global handlers for uncaught
 * JS exceptions and unhandled promise rejections. All errors are POSTed to
 * CUSA_ENDPOINTS.frontendLogs (POST /apps/takeaction/frontend_logs — Guardians API).
 *
 * HOW COVERAGE WORKS — read this before adding new middleware calls:
 *
 *   • cusaFetch (global.js) — logFrontendError is hooked in its catch block, so any
 *     call routed through cusaFetch is logged automatically. No extra work needed.
 *
 *   • Direct fetch() calls — use loggedFetch (global.js) as the preferred wrapper.
 *     It catches network-layer rejections, logs them, and re-throws. HTTP 4xx/5xx
 *     responses still need an explicit window.logFrontendError?.() in the
 *     !response.ok branch — loggedFetch deliberately doesn't touch those.
 *     Current direct-fetch call sites:
 *       - countCtgButtonClick()   → /apps/takeaction/all_clicks           (global.js)
 *       - signPetition()          → /apps/takeaction/petition_signatures  (cusa-section-cusa-article-petition.js)
 *       - fetchShelterData()      → /apps/takeaction/shelters             (cusa-shelter-challenge-search.js)
 *
 * SOURCE-SCOPING FOR GLOBAL HANDLERS
 *
 * The window 'error' and 'unhandledrejection' listeners only log when the
 * originating source matches OUR_ASSET_PATTERN — i.e., the error came from
 * a theme asset under /t/{theme_id}/assets/. Third-party scripts (Elevar,
 * Optable, Shopify analytics, ad pixels) are filtered out at the listener.
 *
 * Accepted tradeoffs:
 *   - "Script error." from CORS-failed scripts drops (empty filename + null stack).
 *   - Inline <script> tag errors in .liquid files drop (filename is the document URL).
 *
 * PAGE CONTEXT (theme_id, theme_name, template, request_path) is injected via
 * window.__pageInfo in sections/header.liquid. Edit that object there if new
 * fields are needed.
 */
(() => {
	const _debounce = new Map();
	const DEBOUNCE_MS = 2000;
	const SUPPRESSED_ERROR_MESSAGES = /failed to fetch|networkerror when attempting to fetch|load failed/i;

	// Matches Shopify theme assets in both URL shapes:
	//   /cdn/shop/t/{id}/assets/...                  (primary-domain proxy, dev, preview)
	//   https://cdn.shopify.com/s/files/.../t/{id}/assets/...  (direct CDN)
	const OUR_ASSET_PATTERN = /\/t\/\d+\/assets\//;
	const isOurAsset = (str) => typeof str === 'string' && OUR_ASSET_PATTERN.test(str);

	/**
	 * Posts a frontend error to the logging endpoint.
	 * Debounced per (error_message + page_url) to suppress duplicate fires within 2 seconds.
	 *
	 * @param {{ error_message: string, error_type?: string, error_stack?: string, page_url?: string, context?: object }} payload
	 */
	const logFrontendError = (payload) => {
		const msg = payload.error_message || 'Unknown error';
		if (SUPPRESSED_ERROR_MESSAGES.test(msg)) return;
		const url = payload.page_url || window.location.href;
		const key = `${msg}::${url}`;
		const now = Date.now();

		if (_debounce.has(key) && now - _debounce.get(key) < DEBOUNCE_MS) return;
		_debounce.set(key, now);

		for (const [k, t] of _debounce) {
			if (now - t > DEBOUNCE_MS * 10) _debounce.delete(k);
		}

		const body = {
			error_message: msg,
			page_url: url,
			theme_id: window.__pageInfo?.theme_id || undefined,
			theme_name: window.__pageInfo?.theme_name || undefined,
			platform: navigator.platform || undefined,
			locale: navigator.language || undefined,
			viewport_width: window.innerWidth || undefined,
			viewport_height: window.innerHeight || undefined,
			screen_width: screen.width || undefined,
			screen_height: screen.height || undefined,
			connection_type: navigator.connection?.effectiveType || undefined
		};

		if (payload.error_type) body.error_type = payload.error_type;
		if (payload.error_stack) body.error_stack = payload.error_stack;
		if (payload.context) body.context = payload.context;

		fetch(CUSA_ENDPOINTS.frontendLogs, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body)
		}).catch(() => {});
	};

	window.logFrontendError = logFrontendError;

	window.addEventListener('error', (event) => {
		if (!isOurAsset(event.filename) && !isOurAsset(event.error?.stack)) return;
		logFrontendError({
			error_message: event.message,
			error_type: 'UncaughtError',
			error_stack: event.error?.stack,
			context: {
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno,
				template: window.__pageInfo?.template,
				request_path: window.__pageInfo?.request_path
			}
		});
	});

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		if (!isOurAsset(reason?.stack)) return;
		logFrontendError({
			error_message: reason?.message || String(reason) || 'Unhandled promise rejection',
			error_type: 'UnhandledRejection',
			error_stack: reason?.stack,
			context: {
				template: window.__pageInfo?.template,
				request_path: window.__pageInfo?.request_path
			}
		});
	});
})();
